import React, { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import { makeRESTApiRequests } from "../../helpers/api";
import { AI_PROMPT_MAX_LENGTH, ENDPOINTS } from "../../helpers/constants";
import AlertModal from "../AlertModal";
import Loader from "../Loader";

const AiPrompt = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingFields, setSavingFields] = useState(false);
  const [content, setContent] = useState("");
  const [savedContent, setSavedContent] = useState("");
  const [meta, setMeta] = useState(null);
  const [fields, setFields] = useState([]);
  const [savedFields, setSavedFields] = useState([]);
  const [availableFields, setAvailableFields] = useState([]);
  const [fieldsDefault, setFieldsDefault] = useState(true);
  const [confirm, setConfirm] = useState(null);

  const applyPrompt = (response) => {
    const nextContent = response?.content || "";
    setContent(nextContent);
    setSavedContent(nextContent);
    setMeta(response);
  };

  const applyFields = (response) => {
    const nextFields = response?.fields || [];
    setFields(nextFields);
    setSavedFields(nextFields);
    setAvailableFields(response?.availableFields || []);
    setFieldsDefault(Boolean(response?.isDefault));
  };

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const [promptResult, fieldsResult] = await Promise.all([
        makeRESTApiRequests({
          endpoint: ENDPOINTS.AI_PROMPT_INQUIRY,
          method: "GET",
        }),
        makeRESTApiRequests({
          endpoint: ENDPOINTS.AI_PROMPT_INQUIRY_FIELDS,
          method: "GET",
        }),
      ]);

      if (cancelled) return;

      if (promptResult.error) toast.error(promptResult.error);
      else applyPrompt(promptResult.response);

      if (fieldsResult.error) toast.error(fieldsResult.error);
      else applyFields(fieldsResult.response);

      setLoading(false);
    };

    load();

    return () => {
      cancelled = true;
    };
  }, []);

  const dirty = content !== savedContent;
  const fieldsDirty =
    fields.length !== savedFields.length ||
    fields.some((field) => !savedFields.includes(field));
  const overLimit = content.length > AI_PROMPT_MAX_LENGTH;

  const runPromptAction = async (request, successMessage) => {
    setSaving(true);
    const { response, error } = await makeRESTApiRequests(request);
    setSaving(false);
    setConfirm(null);

    if (error) {
      toast.error(error);
      return;
    }

    applyPrompt(response);
    toast.success(successMessage);
  };

  const runFieldsAction = async (request, successMessage) => {
    setSavingFields(true);
    const { response, error } = await makeRESTApiRequests(request);
    setSavingFields(false);
    setConfirm(null);

    if (error) {
      toast.error(error);
      return;
    }

    applyFields(response);
    toast.success(successMessage);
  };

  const toggleField = (field) => {
    setFields((prev) =>
      prev.includes(field)
        ? prev.filter((item) => item !== field)
        : [...prev, field]
    );
  };

  const onConfirm = () => {
    if (!confirm || saving || savingFields) return;

    if (confirm === "save") {
      runPromptAction(
        {
          endpoint: ENDPOINTS.AI_PROMPT_INQUIRY,
          method: "PUT",
          requestBody: { content },
        },
        "AI prompt saved."
      );
      return;
    }

    if (confirm === "reset") {
      runPromptAction(
        {
          endpoint: ENDPOINTS.AI_PROMPT_INQUIRY_RESET,
          method: "POST",
        },
        "Prompt reset to the default."
      );
      return;
    }

    if (confirm === "save-fields") {
      runFieldsAction(
        {
          endpoint: ENDPOINTS.AI_PROMPT_INQUIRY_FIELDS,
          method: "PUT",
          requestBody: { fields },
        },
        "Inquiry fields saved."
      );
      return;
    }

    runFieldsAction(
      {
        endpoint: ENDPOINTS.AI_PROMPT_INQUIRY_FIELDS_RESET,
        method: "POST",
      },
      "Inquiry fields reset to the default."
    );
  };

  return (
    <Container fluid className="py-3" style={{ maxWidth: 1200 }}>
      <h5 className="mb-3">AI Prompt</h5>
      {loading ? (
        <Loader />
      ) : (
        <Row className="align-items-stretch">
          <Col lg={8} className="mb-3">
            <Card className="h-100">
              <Card.Body className="d-flex flex-column">
                <h6 className="mb-2">Inquiry system instruction</h6>
                <p className="text-muted small">
                  This text is the system instruction for customer inquiry chat.
                  Saving it changes replies for new questions.
                  {meta?.isDefault
                    ? " The current prompt matches the built-in default."
                    : ""}
                  {meta?.updatedAt
                    ? ` Last saved ${new Date(meta.updatedAt).toLocaleString()}.`
                    : ""}
                </p>
                <Form.Group className="flex-grow-1 d-flex flex-column">
                  <Form.Control
                    as="textarea"
                    rows={18}
                    value={content}
                    maxLength={AI_PROMPT_MAX_LENGTH}
                    onChange={(event) => setContent(event.target.value)}
                    disabled={saving}
                    className="flex-grow-1"
                  />
                  <Form.Text className="text-muted">
                    {content.trim().length} / {AI_PROMPT_MAX_LENGTH} characters
                  </Form.Text>
                </Form.Group>
                <div className="mt-3">
                  <Button
                    variant="primary"
                    className="mr-2"
                    onClick={() => setConfirm("save")}
                    disabled={saving || !dirty || !content.trim() || overLimit}
                  >
                    Save
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="mr-2"
                    onClick={() => setContent(savedContent)}
                    disabled={saving || !dirty}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline-danger"
                    onClick={() => setConfirm("reset")}
                    disabled={saving || (meta?.isDefault && !dirty)}
                  >
                    Reset to default
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={4} className="mb-3">
            <Card className="h-100">
              <Card.Body className="d-flex flex-column">
                <h6 className="mb-2">Inquiry job fields</h6>
                <p className="text-muted small mb-3">
                  Choose which job fields the inquiry AI can use. At least one
                  field is required.
                  {fieldsDefault
                    ? " Currently using the built-in default set."
                    : ""}
                </p>
                <div style={{ overflowY: "auto", maxHeight: 460 }}>
                  {availableFields.map((field) => (
                    <Form.Check
                      key={field}
                      type="checkbox"
                      id={`inquiry-field-${field}`}
                      className="mb-2"
                      label={field}
                      checked={fields.includes(field)}
                      disabled={savingFields}
                      onChange={() => toggleField(field)}
                    />
                  ))}
                </div>
                <div className="mt-3">
                  <Button
                    variant="primary"
                    className="mr-2 mb-2"
                    onClick={() => setConfirm("save-fields")}
                    disabled={savingFields || !fieldsDirty || !fields.length}
                  >
                    Save fields
                  </Button>
                  <Button
                    variant="outline-secondary"
                    className="mr-2 mb-2"
                    onClick={() => setFields(savedFields)}
                    disabled={savingFields || !fieldsDirty}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline-danger"
                    className="mb-2"
                    onClick={() => setConfirm("reset-fields")}
                    disabled={savingFields || (fieldsDefault && !fieldsDirty)}
                  >
                    Reset fields
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <AlertModal
        show={Boolean(confirm)}
        onHide={() => {
          if (!saving && !savingFields) setConfirm(null);
        }}
        onDismissClick={() => {
          if (!saving && !savingFields) setConfirm(null);
        }}
        onContinueClick={onConfirm}
        showProgress={saving || savingFields}
        alertText={
          confirm === "reset"
            ? "This replaces the saved prompt with the built-in default. Unsaved edits are discarded. Continue?"
            : confirm === "reset-fields"
              ? "This restores the default inquiry fields. Continue?"
              : confirm === "save-fields"
                ? "Saving updates which job fields the inquiry AI can use immediately. Continue?"
                : "Saving updates this prompt for every customer inquiry chat immediately. Continue?"
        }
        dismissButtonText="Cancel"
        continueButtonText="Continue"
      />
    </Container>
  );
};

export default AiPrompt;
